export type SkillsImportStatus = 'uploaded' | 'processing' | 'completed' | 'failed' | 'cancelled';

export type SkillsImportFile = {
  name: string;
  contentType: string;
  size: number;
};

export type SkillsImportProficiencyPosition = {
  existingPositionGuid: string | null;
  positionName: string | null;
  matchConfidence: number | null;
};

export type SkillsImportProficiency = {
  level: number;
  name: string;
  description: string | null;
  positions: SkillsImportProficiencyPosition[];
};

export interface SkillsImportSkillGroup {
  id: number | null;
  name: string;
}

export type SkillsImportSkill = {
  matchedSkillId: number | null;
  matchConfidence: number | null;
  name: string;
  description: string | null;
  group: SkillsImportSkillGroup | null;
  warnings: string[];
  proficiencies: SkillsImportProficiency[];
};

export type SkillsImportMessageType = 'positive' | 'info' | 'warning' | 'blocking_error';

export type SkillsImportMessage = {
  type: SkillsImportMessageType;
  text: string;
};

export interface SkillsImportResultGroup {
  id: number | null;
  name: string;
}

export interface SkillsImportResultPosition {
  existingPositionGuid: string | null;
  name: string;
}

export type SkillsImportResult = {
  skills: SkillsImportSkill[];
  messages: SkillsImportMessage[];
  positions: SkillsImportResultPosition[];
  groups: SkillsImportResultGroup[];
  skillsCount?: number;
  positionsCount?: number;
};

export type SkillsImport = {
  id: number;
  status: SkillsImportStatus;
  userPrompt: string | null;
  errorMessage: string | null;
  files: SkillsImportFile[];
  result: SkillsImportResult | null;
  createdAt: string;
  updatedAt: string;
};

export type SkillsImportCreateParams = {
  files: File[];
  userPrompt?: string | null;
};

export interface SkillsImportShowResponse {
  data: SkillsImport;
}

export enum SkillsImportApplyErrorCode {
  ChangesetValidationFailed = 'changeset_validation_failed',
  DuplicateGroupId = 'duplicate_group_id',
  DuplicateNewGroupName = 'duplicate_new_group_name',
  DuplicateMatchedSkillId = 'duplicate_matched_skill_id',
  DuplicateNewSkillName = 'duplicate_new_skill_name',
  DuplicateExistingPositionGuid = 'duplicate_existing_position_guid',
  DuplicateNewPositionName = 'duplicate_new_position_name',
  DuplicateProficiencyLevel = 'duplicate_proficiency_level',
  DuplicateMatchedProficiencyId = 'duplicate_matched_proficiency_id',
  SkillGroupRefNotFound = 'skill_group_ref_not_found',
  ProficiencyPositionRefNotFound = 'proficiency_position_ref_not_found',
  ResourceNotFound = 'resource_not_found',
  PositionNameAlreadyExists = 'position_name_already_exists',
}

export interface SkillsImportApplyError {
  errorCode: SkillsImportApplyErrorCode | string;
  path: string;
  details: Record<string, unknown>;
}

export interface SkillsImportApplyErrorResponse {
  error: {
    code: string;
    message: string;
    errors: SkillsImportApplyError[];
  };
}
